import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { FaFilePdf } from 'react-icons/fa';
import { config } from '../../CommonUtils/CommonURLS';
import { CommonAxiosGet } from '../../CommonUtils/CommonAxios';
import { viewImage } from '../../CommonUtils/viewImage';

const pickDistrictName = (raw = {}) => (
    raw.district_name || raw.dist_name || raw.districtName || raw.distName || raw.districtname || raw.distname || ''
);

const pickDistrictCode = (raw = {}) => (
    raw.district || raw.district_code || raw.dist_code || raw.districtCode || raw.distCode || ''
);

const fmt = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
};

const fmtDate = (v) => {
    if (!v) return null;
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return String(v);
    return dt.toLocaleDateString('en-GB');
};

const Chip = ({ label, type = 'blue' }) => (
    <span className={`preview-chip preview-chip-${type}`}>{label}</span>
);

const OciField = ({ label, value, wide, wide3 }) => {
    const cls = `preview-field${wide ? ' wide' : ''}${wide3 ? ' wide3' : ''}`;
    const isEmpty = value === null || value === undefined;
    return (
        <div className={cls}>
            <div className="preview-field-label">{label}</div>
            <div className={`preview-field-val${isEmpty ? ' empty' : ''}`}>
                {isEmpty ? '-' : value}
            </div>
        </div>
    );
};

const OciSection = ({ icon, iconBg, title, sub, children }) => (
    <div className="preview-card">
        <div className="preview-card-hdr">
            <div className="preview-card-hdr-left">
                <div className="preview-card-icon" style={{ background: iconBg }}>{icon}</div>
                <div>
                    <div className="preview-card-title">{title}</div>
                    {sub && <div className="preview-card-sub">{sub}</div>}
                </div>
            </div>
        </div>
        {children}
    </div>
);

const DocButton = ({ value }) => {
    if (!value) {
        return <span className="preview-empty-doc">Not Uploaded</span>;
    }

    return (
        <button
            type="button"
            className="preview-doc-link"
            onClick={() => viewImage(value)}
        >
            <FaFilePdf />
            View File
        </button>
    );
};

const mapApiToViewModel = (raw = {}, districtList = []) => {
    const districtNameFromApi = pickDistrictName(raw);
    const districtCode = String(pickDistrictCode(raw) || '').trim();
    const districtFromMaster = districtList.find((item) => String(item?.dist_code || item?.district_code || '').trim() === districtCode);

    return ({
        applicationNo: raw.application_no || raw.applicationNo || '',
        collegeName: raw.college_name || raw.collegeName || '',
        studentName: raw.student_name || raw.studentName || '',
        fathersName: raw.fathers_name || raw.fathersName || '',
        district: districtNameFromApi || districtFromMaster?.dist_name || districtFromMaster?.district_name || districtCode,
        gender: raw.gender || '',
        countryOfOrigin: raw.country_of_origin || raw.countryOfOrigin || '',
        passportNumber: raw.passport_number || raw.passportNumber || '',
        passportIssueDate: raw.passport_issue_date || raw.passportIssueDate || '',
        passportExpiryDate: raw.passport_expiry_date || raw.passportExpiryDate || '',
        visaIssueDate: raw.visa_issue_date || raw.visaIssueDate || '',
        visaExpiryDate: raw.visa_expiry_date || raw.visaExpiryDate || '',
        dateOfBirth: raw.date_of_birth || raw.dateOfBirth || '',
        courseCategory: raw.course || raw.courseCategory || '',
        courseName: raw.branch || raw.courseName || '',
        category: raw.category || '',
        status: raw.status || '',
        antecedentsApproved: raw.antecedents_approved || raw.antecedentsApproved || '',
        academicCredentialsVerified: raw.academic_credentials_verified || raw.academicCredentialsVerified || '',
        finalApproval: raw.final_approval || raw.finalApproval || '',
        photo: raw.photo || '',
        passport: raw.passport_doc || raw.passport || '',
        visa: raw.visa_doc || raw.visa || '',
        ssc: raw.ssc_doc || raw.ssc || '',
        sscEmbassyCertificate: raw.ssc_embassy_certificate || raw.sscEmbassyCertificate || '',
        sscAPStateBoardCertificate: raw.ssc_ap_state_board_certificate || raw.sscAPStateBoardCertificate || '',
        inter: raw.inter_doc || raw.inter || '',
        interEmbassyCertificate: raw.inter_embassy_certificate || raw.interEmbassyCertificate || '',
        interAPStateBoardCertificate: raw.inter_ap_state_board_certificate || raw.interAPStateBoardCertificate || '',
        degree: raw.degree_doc || raw.degree || '',
        degreeEmbassyCertificate: raw.degree_embassy_certificate || raw.degreeEmbassyCertificate || '',
        degreeAPStateBoardCertificate: raw.degree_ap_state_board_certificate || raw.degreeAPStateBoardCertificate || '',
        tc: raw.tc_doc || raw.tc || '',
        others: raw.others_doc || raw.others || '',
        antecedentsApprovedDoc: raw.antecedents_approved_doc || raw.antecedentsApprovedDoc || '',
        academicCredentialsApproved: raw.academic_credentials_approved_doc || raw.academicCredentialsApproved || '',
        finalApprove: raw.final_approve_doc || raw.finalApprove || '',
    });
};

export default function OverseasFormPreview({ userId, studentData, onBack }) {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [admission, setAdmission] = useState(null);
    const [districtList, setDistrictList] = useState([]);
    const printRef = useRef(null);

    const selectedStudentData = studentData || location.state?.studentData || null;

    const handlePrint = useReactToPrint({
        contentRef: printRef,
    });

    useEffect(() => {
        CommonAxiosGet(`${config.url.STU_URL}getdistricts`)
            .then((res) => {
                const districts = res?.data?.scode === '01' ? (res?.data?.districtmst || []) : [];
                setDistrictList(Array.isArray(districts) ? districts : []);
            })
            .catch(() => setDistrictList([]));
    }, []);

    useEffect(() => {
        if (selectedStudentData) {
            setAdmission(mapApiToViewModel(selectedStudentData, districtList));
            setLoading(false);
            return;
        }

        const login = userId || sessionStorage.getItem('user_id');
        if (!login) {
            setLoading(false);
            setAdmission(null);
            return;
        }

        setLoading(true);
        CommonAxiosGet(`${config.url.STU_URL}getOciAdmissionDetails/${login}`)
            .then((res) => {
                const row = res?.data?.scode === '01' && res?.data?.data?.length > 0
                    ? res.data.data[0]
                    : null;
                setAdmission(row ? mapApiToViewModel(row, districtList) : null);
            })
            .catch(() => {
                setAdmission(null);
            })
            .finally(() => setLoading(false));
    }, [districtList, selectedStudentData, userId]);

    const docs = useMemo(() => {
        if (!admission) return [];
        return [
            // { label: 'Photo', value: admission.photo },
            { label: 'Passport', value: admission.passport },
            { label: 'Visa', value: admission.visa },
            { label: 'SSC', value: admission.ssc },
            { label: 'SSC Embassy Certificate', value: admission.sscEmbassyCertificate },
            { label: 'SSC AP State Board Certificate', value: admission.sscAPStateBoardCertificate },
            { label: 'INTER', value: admission.inter },
            { label: 'INTER Embassy Certificate', value: admission.interEmbassyCertificate },
            { label: 'INTER AP State Board Certificate', value: admission.interAPStateBoardCertificate },
            { label: 'DEGREE', value: admission.degree },
            { label: 'DEGREE Embassy Certificate', value: admission.degreeEmbassyCertificate },
            { label: 'DEGREE AP State Board Certificate', value: admission.degreeAPStateBoardCertificate },
            { label: 'TC', value: admission.tc },
            { label: 'Others', value: admission.others },
        ];
    }, [admission]);

    if (loading) {
        return (
            <div className="preview-root">
                <div className="preview-center">
                    <div className="preview-loader">Loading preview...</div>
                </div>
            </div>
        );
    }

    if (!admission) {
        return (
            <div className="preview-root">
                <div className="preview-center">
                    <div className="preview-loader">No Overseas admission details found.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="preview-root">
            <div ref={printRef}>
                <div className="preview-page-hdr">
                    <div>
                        <div className="preview-page-title">Overseas Admission Details</div>
                        <div className="preview-page-sub">Submitted overseas admission application details</div>
                    </div>
                    <div className="preview-action-row preview-no-print">
                        <button className="preview-btn preview-btn-navy" onClick={handlePrint}>
                            Print
                        </button>
                        <button className="btn btn-secondary" onClick={onBack}>
                            Back
                        </button>
                    </div>
                </div>
                <OciSection
                    icon="👨‍🎓"
                    iconBg="#dbeafe"
                    title="Personal Information"
                    sub="Basic student and admission details"
                >
                    <div className="preview-field-grid">
                        <OciField label="Application Number" value={fmt(admission?.applicationNo)} />
                        <OciField label="College Name" value={fmt(admission?.collegeName)} />
                        <OciField label="Student Name" value={fmt(admission?.studentName)} />
                        <OciField label="Father's Name" value={fmt(admission?.fathersName)} />
                        <OciField label="District" value={fmt(admission?.district)} />
                        <OciField label="Gender" value={<Chip label={fmt(admission?.gender) || '-'} type="purple" />} />
                        <OciField label="Country of Origin" value={fmt(admission?.countryOfOrigin)} />
                        <OciField label="Date of Birth" value={fmtDate(admission?.dateOfBirth)} />
                    </div>
                </OciSection>

                <OciSection
                    icon="🛂"
                    iconBg="#ede9fe"
                    title="Passport and Visa Information"
                    sub="Travel and immigration details"
                >
                    <div className="preview-field-grid">
                        <OciField label="Passport Number" value={fmt(admission?.passportNumber)} />
                        <OciField label="Passport Issue Date" value={fmtDate(admission?.passportIssueDate)} />
                        <OciField label="Passport Expiry Date" value={fmtDate(admission?.passportExpiryDate)} />
                        <OciField label="Visa Issue Date" value={fmtDate(admission?.visaIssueDate)} />
                        <OciField label="Visa Expiry Date" value={fmtDate(admission?.visaExpiryDate)} />
                    </div>
                </OciSection>

                <OciSection
                    icon="📚"
                    iconBg="#fef9c3"
                    title="Academic Information"
                    sub="Course and approval details"
                >
                    <div className="preview-field-grid">
                        <OciField label="Course Category" value={fmt(admission?.courseCategory)} />
                        <OciField label="Course Name" value={fmt(admission?.courseName)} />
                        <OciField label="Category" value={<Chip label={fmt(admission?.category) || '-'} type="amber" />} />
                        <OciField label="Photo" value={<DocButton value={admission?.photo} />} />
                        <OciField label="Status" value={<Chip label={fmt(admission?.status) || 'Pending'} type="green" />} />
                        <OciField
                            label="Antecedents Approved or not"
                            value={<Chip label={fmt(admission?.antecedentsApproved) || '-'} type={admission?.antecedentsApproved === 'Yes' ? 'green' : 'red'} />}
                        />
                        <OciField
                            label="Academic Credentials Verified or not"
                            value={<Chip label={fmt(admission?.academicCredentialsVerified) || '-'} type={admission?.academicCredentialsVerified === 'Yes' ? 'green' : 'red'} />}
                        />
                        <OciField
                            label="Final Approval"
                            value={<Chip label={fmt(admission?.finalApproval) || '-'} type={admission?.finalApproval === 'Yes' ? 'green' : 'red'} />}
                        />
                        <OciField label="Antecedents Approved Document" value={<DocButton value={admission?.antecedentsApprovedDoc} />} wide />
                        <OciField label="Academic Credentials Approved Document" value={<DocButton value={admission?.academicCredentialsApproved} />} wide />
                        <OciField label="Final Approve Document" value={<DocButton value={admission?.finalApprove} />} wide />
                    </div>
                </OciSection>

                <OciSection
                    icon="📎"
                    iconBg="#fee2e2"
                    title="Uploaded Documents"
                    sub="Documents submitted in the Overseas admission form"
                >
                    <div className="preview-field-grid">
                        {docs.map((doc) => (
                            <OciField key={doc.label} label={doc.label} value={<DocButton value={doc.value} />} />
                        ))}
                    </div>
                </OciSection>
            </div>
        </div>
    );
}
